<script setup lang="ts">
import { computed, PropType, ref, toRefs, watch } from "vue";
import VerticalOrDimensionSelection, {
	AxisItem,
} from "@/modules/explorer/components/AxisConfiguration/Selection.vue";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import DimensionDO = App.Data.DimensionDO;
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import GridConfigurationDimensionOrder from "@/modules/explorer/components/Metrics/GridConfigurationDimensionOrder.vue";
import { useTabCycling } from "@/ui/composables/useTabCycling";
import Modal from "@/ui/components/modal.vue";
import { summarizeSelection } from "@/modules/explorer/components/AxisConfiguration/summarizeSelection";
import { t } from "@/plugins/i18n";
import { pivotRowKey } from "@/modules/explorer/components/Metrics/MetricHelper";

const activeAxisTab = defineModel<"horizontal" | "metrics">();

const props = defineProps({
	isVisible: {
		type: Boolean,
		require: true,
	},
	dataTableToEdit: {
		type: Object as PropType<App.Data.Explorer.DataTableDO>,
		require: false,
	},
});

const visible = computed({
	get() {
		return props.isVisible;
	},
	set(newValue) {
		if (!newValue) {
			emit("close-dialog");
		}
	},
});

const { dataTableToEdit } = toRefs(props);

const search = ref("");

const initialSplitPrediction = dataTableToEdit.value.split_prediction;
const initialComparisonNested = dataTableToEdit.value.is_comparison_nested;
const initialMetrics = dataTableToEdit.value.metrics.map(
	(metric) => metric.name_clean
);

const DataTableConfigurationTabs = [
	{
		nameClean: "horizontal",
		labelKey: "dimensions",
		icon: "fa: fa-light fa-table-rows",
		selectMode: "multiple",
	},
	{
		nameClean: "metrics",
		labelKey: "metrics",
		icon: "fa: fa-light fa-table-columns",
		selectMode: "multiple",
	},
];

const emit = defineEmits(["update", "close-dialog"]);

const selectMode = computed(() => {
	return (
		DataTableConfigurationTabs.find(
			(tab) => tab.nameClean === activeAxisTab.value
		)?.["selectMode"] ?? "single"
	);
});

const items = computed<AxisItem[]>(() => {
	const propertyStore = usePropertyStore();
	if (activeAxisTab.value === "metrics") {
		return propertyStore.verticalsForDataTable;
	}

	return propertyStore.dimensions;
});

function arraysEqual(a: any[], b: any[]) {
	if (a.length !== b.length) return false;

	const sortedA = [...a].sort();
	const sortedB = [...b].sort();

	return sortedA.every((val, index) => val === sortedB[index]);
}

const currentMetrics = computed(() =>
	dataTableToEdit.value.metrics.map((metric) => metric.name_clean)
);

const metricsChanged = computed(
	() => !arraysEqual(initialMetrics, currentMetrics.value)
);

const configurationTabs = computed(() =>
	DataTableConfigurationTabs.map((tab) => ({
		...tab,
		disabled: isConfigurationValid.value,
	}))
);

const { cycleAxisTab } = useTabCycling(
	() => configurationTabs.value,
	activeAxisTab
);

const closeDialog = () => {
	const resetNeeded =
		initialSplitPrediction !== dataTableToEdit.value.split_prediction ||
		initialComparisonNested !== dataTableToEdit.value.is_comparison_nested ||
		metricsChanged.value;

	if (resetNeeded) {
		dataTableToEdit.value.hidden_columns = [];
	}

	emit("update", dataTableToEdit.value);
	emit("close-dialog");
};

const documentAttribute = computed<
	DocumentVerticalDTO[] | DimensionDO | DocumentDimensionDTO[]
>({
	get() {
		switch (activeAxisTab.value) {
			case "horizontal":
				return dataTableToEdit.value.dimensions;
			// return dataTableToEdit.value.horizontal_dimension;
			case "metrics":
				return dataTableToEdit.value.metrics;
		}
		return null;
	},
	set(newValue) {
		switch (activeAxisTab.value) {
			case "horizontal":
				dataTableToEdit.value.dimensions = newValue as DocumentDimensionDTO[];
				break;
			case "metrics":
				dataTableToEdit.value.metrics = newValue as DocumentVerticalDTO[];
				break;
		}
	},
});

watch(activeAxisTab, () => {
	search.value = "";
});

const isConfigurationValid = computed(() => {
	const { dimensions, metrics } = dataTableToEdit.value;
	return (
		dimensions.length === 0 ||
		metrics.length === 0 ||
		(pivotRowKey(dataTableToEdit.value) !== null && metrics.length !== 1)
	);
});

const activeTabLabel = computed(() =>
	t(
		`axisDialog.axis.${
			activeAxisTab.value === "metrics" ? "metrics" : "dimensions"
		}`
	)
);

const selectionLabel = computed(() =>
	summarizeSelection(documentAttribute.value, { t })
);
</script>

<template>
	<modal
		v-model:visible="visible"
		:title="t('axisDialog.titleGrid')"
		icon="fa-regular fa-table-columns"
		width="62rem"
		no-body-padding
		:persistent="false"
	>
		<div @keydown.tab="cycleAxisTab">
			<v-tabs
				v-model="activeAxisTab"
				class="axis-tabs flex-shrink-0"
				bg-color="secondary"
				height="40"
				center-active
				show-arrows
			>
				<v-tab
					v-for="tab in configurationTabs"
					:disabled="tab.disabled"
					:key="tab.nameClean"
					:value="tab.nameClean"
					class="axis-tab"
				>
					<v-icon :icon="tab.icon" size="16" />
					<span>{{ t(`axisDialog.axis.${tab.labelKey}`) }}</span>
				</v-tab>
			</v-tabs>
			<div class="body">
				<vertical-or-dimension-selection
					v-model="documentAttribute"
					v-model:search="search"
					:data-table="dataTableToEdit"
					:select-mode="selectMode"
					:axis-name-clean="activeAxisTab"
					:items="items"
				/>
				<div class="order-pane pa-3">
					<GridConfigurationDimensionOrder
						v-model:data-table="dataTableToEdit"
						v-model:items="documentAttribute"
						:metrics-changed="metricsChanged"
					/>
				</div>
			</div>
		</div>
		<template #actions>
			<div class="footer">
				<p class="summary text-body-2">
					<span class="seg-axis">{{ activeTabLabel }}</span>
					<span class="seg-selection">{{ selectionLabel }}</span>
				</p>
				<v-btn variant="text" @click="emit('close-dialog')">
					{{ t("axisDialog.action.close") }}
				</v-btn>
				<v-btn
					:disabled="isConfigurationValid"
					prepend-icon="fa: fa-regular fa-check"
					@click="closeDialog"
				>
					{{ t("axisDialog.action.save") }}
				</v-btn>
			</div>
		</template>
	</modal>
</template>

<style scoped lang="scss">
/* Three panes that each scroll on their own, like the FilterDialog body: the
   rail and the item list come from Selection.vue, the column order sits on the
   trailing edge. */
.body {
	display: flex;
	height: 64vh;
	min-height: 0;
	overflow: hidden;
}

.order-pane {
	flex: 0 0 19rem;
	min-width: 0;
	overflow-y: auto;
	border-left: 1px solid rgba(var(--color-on-background), 0.2);
}

.axis-tabs {
	:deep(.v-tab.axis-tab) {
		min-width: 0;
		padding-inline: 0.75rem;
		text-transform: none;
		letter-spacing: normal;
		font-size: 0.875rem;

		.v-btn__content {
			gap: 0.375rem;
		}
	}
}

.footer {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
}

.summary {
	flex: 1 1 auto;
	display: flex;
	align-items: baseline;
	gap: 0.375rem;
	margin: 0;
	min-width: 0;
	color: rgb(var(--color-on-background));
}

.seg-axis::after {
	content: ":";
}

.seg-selection {
	font-weight: 600;
	color: rgb(var(--color-on-surface));
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
